/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React, { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { Calendar } from '@services/requests/housien-models/Calendar';
import { Text, useSonnerToast } from '@/shared/ui';
import Button from '@/shared/ui/components/basic/Button';
import { reportError } from '@/shared/ui/components/basic/error-notification/report-error';
import { useGoogleConnect } from '@features/agenda/hooks/useGoogleConnect';
import { useGoogleSync } from '@features/agenda/hooks/useGoogleSync';
import { useGoogleDisconnect } from '@features/agenda/hooks/useGoogleDisconnect';
import { useGoogleSettings } from '@features/agenda/hooks/useGoogleSettings';
import type { GoogleSettingsPatch } from '@features/agenda/hooks/useGoogleSettings';
import { agendaKeys } from '@features/agenda/hooks/agenda-keys';
import {
  googleAccountEmail,
  googleConnectionState,
  ownGoogleCalendar,
} from '@features/agenda/utils/google-connection';
import { GoogleCalendarConnectDisclosure } from './GoogleCalendarConnectDisclosure';
import { ConnectedSections } from './GoogleCalendarPane.connected';
import {
  AccountCard,
  DirectionTile,
  GoogleButton,
  PrivacyLink,
  StateBanner,
} from './GoogleCalendarPane.parts';


/**
 * What the account card says beneath the address.
 *
 * A card reading "connected" over a link Google no longer accepts is the
 * part that misleads: the credential is there, it just does not work.
 *
 * @param t - Translator.
 * @param syncing - Whether a sync is running.
 * @param broken - Whether the link stopped working.
 * @returns The status line.
 */
const _accountStatus = (
  t: (key: string) => string,
  syncing: boolean,
  broken: boolean,
): string => {
  if (broken) return t('integrations.error_title');
  return syncing ? t('integrations.syncing') : t('integrations.connected');
};

export interface GoogleCalendarPaneProps {
  agencyId: string;
  currentUserEmail: string;
  calendars: Calendar[];
}

/**
 * The "Google Agenda" section of the calendar settings.
 *
 * One pane, four states. The link is per user: a calendar a teammate synced
 * never reads as connected here, because the actions on this screen act on
 * credentials and a colleague's are not the user's to spend.
 *
 * @param props - Agency, signed-in user and the visible calendars.
 * @returns The rendered pane.
 */
export const GoogleCalendarPane: React.FC<GoogleCalendarPaneProps> = ({
  agencyId,
  currentUserEmail,
  calendars,
}) => {
  const { t } = useTranslation('features/agenda');
  const toast = useSonnerToast();
  const queryClient = useQueryClient();
  const [disclosureOpen, setDisclosureOpen] = useState(false);

  const connection = ownGoogleCalendar(calendars, currentUserEmail);
  const state = googleConnectionState(connection);
  const connected = state !== 'disconnected';
  const canWrite = state === 'connected';

  const { connect, isConnecting } = useGoogleConnect(agencyId);
  const { sync, isSyncing } = useGoogleSync(agencyId);
  const { disconnect, isDisconnecting } = useGoogleDisconnect(agencyId);
  const settingsQuery = useGoogleSettings(agencyId, connected);
  const broken = state === 'error' || settingsQuery.isError;

  const handleSave = useCallback(
    (patch: GoogleSettingsPatch) => {
      settingsQuery
        .save(patch)
        .then(() => toast.showSuccess(t('integrations.saved')))
        .catch((error) => reportError(error));
    },
    [settingsQuery, toast, t],
  );

  const handleSync = useCallback(() => {
    sync()
      .then(() =>
        queryClient.invalidateQueries({ queryKey: agendaKeys.all(agencyId) }),
      )
      .catch((error) => reportError(error));
  }, [sync, queryClient, agencyId]);

  const handleDisconnect = useCallback(() => {
    disconnect()
      .then(() => queryClient.invalidateQueries({ queryKey: agendaKeys.all(agencyId) }))
      .catch((error) => reportError(error));
  }, [disconnect, queryClient, agencyId]);

  const personalCalendars = calendars.filter(
    (c) => c.owner_id === currentUserEmail && !c.sync?.google,
  );

  return (
    <section
      role="tabpanel"
      aria-label={t('calendar_settings.nav_google')}
      className="flex flex-col gap-(--space-6)"
    >
      <header className="flex flex-col gap-(--space-1)">
        <Text
          component="h2"
          className="m-0 text-[length:var(--text-app-section)] font-[weight:var(--font-semibold)] text-[var(--text-primary)]"
        >
          {t('calendar_settings.nav_google')}
        </Text>
        <Text className="text-[length:var(--text-small)] text-[var(--text-secondary)] text-pretty">
          {t('integrations.pane_subtitle')}
        </Text>
      </header>

      {state === 'readonly' && (
        <StateBanner
          tone="warning"
          title={t('integrations.readonly_title')}
          body={t('integrations.readonly_body')}
          cta={t('integrations.readonly_cta')}
          onAction={() => setDisclosureOpen(true)}
        />
      )}
      {(state === 'error' || settingsQuery.isError) && (
        <StateBanner
          tone="error"
          title={t('integrations.error_title')}
          body={t('integrations.error_body')}
          cta={t('integrations.reconnect')}
          onAction={() => setDisclosureOpen(true)}
        />
      )}

      {!connected && (
        <div className="flex flex-col gap-(--space-4)">
          <div className="grid gap-(--space-3) md:grid-cols-2">
            <DirectionTile
              direction={t('integrations.direction_in')}
              headline={t('integrations.direction_in_headline')}
              detail={t('integrations.direction_in_detail')}
            />
            <DirectionTile
              direction={t('integrations.direction_out')}
              headline={t('integrations.direction_out_headline')}
              detail={t('integrations.direction_out_detail')}
            />
          </div>
          <PrivacyLink label={t('integrations.disclosure_privacy_link')} />
          <div>
            <GoogleButton
              label={t('integrations.continue_with_google')}
              loading={isConnecting}
              onClick={() => setDisclosureOpen(true)}
            />
          </div>
        </div>
      )}

      {connected && (
        <AccountCard
          email={googleAccountEmail(connection) ?? currentUserEmail}
          status={_accountStatus(t, isSyncing, broken)}
          syncLabel={t('integrations.google_refresh')}
          syncing={isSyncing || broken}
          onSync={handleSync}
        />
      )}

      {connected && !settingsQuery.isError && settingsQuery.settings && (
        <ConnectedSections
          settings={settingsQuery.settings}
          canWrite={canWrite && !broken}
          personalCalendars={personalCalendars}
          t={t}
          onSave={handleSave}
        />
      )}

      {connected && (
        <section className="flex flex-col gap-(--space-2) border-t border-[var(--border-subtle)] pt-(--space-4)">
          <Text className="text-[length:var(--text-small)] text-[var(--text-secondary)] text-pretty">
            {t('integrations.disconnect_body')}
          </Text>
          <div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              loading={isDisconnecting}
              disabled={isDisconnecting}
              onClick={handleDisconnect}
              className="text-[var(--status-error-text)] hover:bg-[var(--button-danger-ghost-hover-bg)]"
            >
              {t('integrations.google_disconnect')}
            </Button>
          </div>
        </section>
      )}

      <GoogleCalendarConnectDisclosure
        opened={disclosureOpen}
        isConnecting={isConnecting}
        onClose={() => setDisclosureOpen(false)}
        onConfirm={() => {
          connect().catch((error) => reportError(error));
        }}
      />
    </section>
  );
};
